import React from 'react';
import { Tooltip } from 'antd';
import { useHistory } from 'react-router-dom';
import { IntegrationConfig, MetaItem } from './types';

const Fact = ({ item }: { item: MetaItem }) => {
    const empty = item.value === undefined || item.value === null || item.value === '';
    return (
        <Tooltip placement="topLeft" title={item.title ?? (typeof item.value === 'string' ? item.value : undefined)}>
            <span className={`nio-fact${item.mono ? ' nio-fact--mono' : ''}`}>
                <span className="nio-fact__label">{item.label}</span>
                <span className={`nio-fact__value${empty ? ' nio-fact__value--empty' : ''}`}>
                    {empty ? '—' : item.value}
                </span>
            </span>
        </Tooltip>
    );
};

/**
 * One integration, as a full-width band: identity, state and controls on the
 * top line, everything factual on the line below. Deliberately unlike the
 * Service Items cards — these are configuration records, not inventory.
 *
 * The band itself opens the record, so there is no button saying so — the
 * controls on it act in place and stop the click from reaching it.
 */
export const IntegrationRow = ({
    record,
    config,
    refresh,
}: {
    record: any;
    config: IntegrationConfig;
    refresh: () => void;
}) => {
    const { Actions } = config;
    const history = useHistory();
    const status = config.status?.(record);
    const subtitle = config.subtitle?.(record);
    const title = config.title(record);

    const open = () => history.push(`${config.basePath}/${record.id}`);

    return (
        <article
            className={`nio-item${status ? ` nio-item--${status.tone}` : ''}`}
            role="button"
            tabIndex={0}
            aria-label={`Open ${title}`}
            onClick={open}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    open();
                }
            }}
        >
            <div className="nio-item__top">
                <h3 className="nio-item__name" title={title}>
                    {title}
                </h3>

                {status && <span className={`nio-pill nio-pill--${status.tone}`}>{status.label}</span>}
                {config.badges?.(record)}

                {/* these act on the record in place, so the click stops here
                    rather than opening the band underneath them */}
                <div
                    className="nio-item__actions"
                    role="presentation"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    <Actions record={record} refresh={refresh} />
                </div>
            </div>

            {subtitle && <p className="nio-item__sub">{subtitle}</p>}

            <div className="nio-item__facts">
                {config.meta(record).map((item) => (
                    <Fact key={item.label} item={item} />
                ))}
            </div>
        </article>
    );
};

export default IntegrationRow;
